/**
 * @file Components/Sections/TourCard.jsx
 * @description Tour card component (Sections variant). Renders a tour preview
 * with image, title, price, duration, and link to tour details.
 */

import React from 'react';
import PropTypes from 'prop-types';

const TourCard = ({ tour, onBookNow, onViewDetails }) => {
  if (!tour) {
    return null;
  }

  const handleBookNow = () => {
    if (onBookNow) {
      onBookNow(tour);
    }
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(tour);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {tour.image && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={tour.image}
            alt={tour.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
          {tour.featured && (
            <div className="absolute top-4 left-4">
              <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                Featured
              </span>
            </div>
          )}
          {tour.discount && (
            <div className="absolute top-4 right-4">
              <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                {tour.discount}% OFF
              </span>
            </div>
          )}
        </div>
      )}
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          {tour.category && (
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
              {tour.category}
            </span>
          )}
          {tour.rating && (
            <div className="flex items-center">
              <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm text-gray-600">{tour.rating}</span>
            </div>
          )}
        </div>

        <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-blue-600 transition-colors duration-200">
          {tour.title}
        </h3>
        
        {tour.description && (
          <p className="text-gray-600 mb-4 line-clamp-2">
            {tour.description}
          </p>
        )}

        <div className="space-y-2 mb-4">
          {tour.duration && (
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {tour.duration}
            </div>
          )}
          
          {tour.location && (
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {tour.location}
            </div>
          )}

          {tour.groupSize && (
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Max {tour.groupSize} people
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            {tour.originalPrice && tour.originalPrice !== tour.price && (
              <span className="text-sm text-gray-500 line-through mr-2">
                ₹{tour.originalPrice}
              </span>
            )}
            <span className="text-2xl font-bold text-blue-600">
              ₹{tour.price}
            </span>
            {tour.priceUnit && (
              <span className="text-sm text-gray-600 ml-1">
                /{tour.priceUnit}
              </span>
            )}
          </div>
          
          {tour.availability && (
            <span className={`text-xs px-2 py-1 rounded-full ${
              tour.availability === 'available' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {tour.availability === 'available' ? 'Available' : 'Sold Out'}
            </span>
          )}
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleViewDetails}
            className="flex-1 border border-blue-600 text-blue-600 py-2 px-4 rounded-md hover:bg-blue-50 transition-colors duration-200"
          >
            View Details
          </button>
          <button
            onClick={handleBookNow}
            disabled={tour.availability === 'sold-out'}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

TourCard.propTypes = {
  tour: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    image: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    originalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priceUnit: PropTypes.string,
    duration: PropTypes.string,
    location: PropTypes.string,
    groupSize: PropTypes.number,
    rating: PropTypes.number,
    category: PropTypes.string,
    featured: PropTypes.bool,
    discount: PropTypes.number,
    availability: PropTypes.oneOf(['available', 'sold-out'])
  }).isRequired,
  onBookNow: PropTypes.func,
  onViewDetails: PropTypes.func
};

export default TourCard;
